import { useEffect, useState, useRef, ReactNode } from 'react';

interface Props {
  children: ReactNode[];
}

export const LogoSlider = ({ children }: Props) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResettingRef = useRef(false);

  // Create multiple duplicates for seamless infinite effect (at least 3 sets)
  const extendedLogos = [...children, ...children, ...children];
  const itemWidth = 14; // rem

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Don't auto-advance if user prefers reduced motion
    if (prefersReducedMotion) return;

    const interval = setInterval(() => {
      setSlideIndex((oldIndex) => oldIndex + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  // Handle seamless reset when we reach the end of first set
  useEffect(() => {
    if (slideIndex >= children.length && !isResettingRef.current) {
      isResettingRef.current = true;

      // Disable transition and instantly reset transform
      if (containerRef.current) {
        containerRef.current.style.transition = 'none';

        containerRef.current.style.transform = 'translateX(0%)';
      }

      // Re-enable transition and reset index on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.style.transition = 'transform 0.5s ease';
          }
          isResettingRef.current = false;

          setSlideIndex(1); // Continue from 1 (we visually reset to 0)
        });
      });
    }
  }, [slideIndex, children.length]);

  // Calculate transform percentage
  const transformPercentage = (100 / extendedLogos.length) * slideIndex;

  // Determine which logos are duplicates (for aria-hidden)
  // Only the first set should be accessible to screen readers
  const isDuplicate = (index: number) => index >= children.length;

  return (
    <div
      role="region"
      aria-label="Partner and client logos"
      aria-live="polite"
      aria-atomic="false"
    >
      <div
        className="flex relative overflow-hidden justify-center before:bg-gradient-to-r before:left-0 after:right-0 before:from-background-primary before:to-transparent before:absolute before:w-[9.375rem] before:h-full before:top-0 before:left-0 after:bg-gradient-to-l after:from-background-primary after:to-transparent after:absolute after:w-[9.375rem] after:h-full after:top-0 after:right-0 before:pointer-events-none after:pointer-events-none before:z-[1] after:z-[1]"
        aria-hidden="false"
      >
        <div
          ref={containerRef}
          className="flex items-center justify-center py-5"
          role="list"
          aria-label="Logo carousel"
          style={{
            width: `calc(${itemWidth}rem * ${extendedLogos.length})`,
            transform: prefersReducedMotion
              ? 'translateX(0%)'
              : `translateX(-${transformPercentage}%)`,
            transition:
              isResettingRef.current || prefersReducedMotion
                ? 'none'
                : 'transform 0.5s ease',
          }}
        >
          {extendedLogos.map((logo, index) => (
            <div
              style={{ width: `${itemWidth}rem` }}
              className="w-auto flex items-center justify-center px-10"
              key={index}
              role="listitem"
              aria-hidden={isDuplicate(index) ? 'true' : 'false'}
            >
              {logo}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
